import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../constants";
import { useClub } from "./ClubContext";
import { useSeason } from "./SeasonContext";
import { Game, Season } from "../types/ScheduleTypes";
import { Club } from "../types/ClubTypes";
import { Standing } from "../types/ScheduleTypes";

interface ScheduleContextType {
  games: Game[];
  standings: Standing[];
  clubs: Club[];
  isLoading: boolean;
  error: string | null;
  fetchGames: (clubId: number) => Promise<void>;
  fetchStandings: (season: Season | null) => Promise<void>;
  fetchClubs: () => Promise<void>;
  updateGame: (gameId: number, gameData: Partial<Game>, clubId: number) => Promise<void>;
  createGame: (gameData: Partial<Game>, clubId: number) => Promise<void>;
  deleteGame: (gameId: number, clubId: number) => Promise<void>;
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(
  undefined
);

export function ScheduleProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);

  const { club } = useClub();
  const { selectedSeason } = useSeason();

  useEffect(() => {
    if (club) {
      fetchStandings(selectedSeason || null);
      fetchGames(club.id);
      fetchClubs();
    }
  }, [club, selectedSeason]);

  const fetchGames = async (clubId: number) => {
    if (!club) return;
    if (!selectedSeason) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/games/schedule/`, {
        params: {
          season_id: selectedSeason.id,
          club_id: clubId,
        },
      });
      setGames(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching games");
      console.error("Error fetching games:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStandings = async (season: Season | null) => {
    if (!season) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/games/standings/`, {
        params: { season_id: season.id },
      });
      setStandings(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching standings");
      console.error("Error fetching standings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clubs/`);
      setClubs(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching clubs");
      console.error("Error fetching clubs:", err);
    }
  };

  const updateGame = async (gameId: number, gameData: Partial<Game>, clubId: number) => {
    setIsLoading(true);
    setError(null);
    console.log(gameData);
    try {
      await axios.patch(`${API_BASE_URL}/games/${gameId}/`, gameData);
      await fetchGames(clubId);
      await fetchStandings(selectedSeason || null);
    } catch (err: any) {
      setError(err.message || "An error occurred while updating the game");
      console.error("Error updating game:", err);
      throw err; // Propagate error to component for handling
    } finally {
      setIsLoading(false);
    }
  };

  const createGame = async (gameData: Partial<Game>, clubId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      // Add season to the game data
      const newGameData = {
        ...gameData,
        season: selectedSeason?.id,
      };
      console.log(newGameData);

      await axios.post(`${API_BASE_URL}/games/`, newGameData);
      // Refresh the games list after creation
      await fetchGames(clubId);
      await fetchStandings(selectedSeason || null);
    } catch (err: any) {
      setError(err.message || "An error occurred while creating the game");
      console.error("Error creating game:", err);
      throw err; // Propagate error to component for handling
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGame = async (gameId: number, clubId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/games/${gameId}/`);
      // Refresh the games list after deletion
      await fetchGames(clubId);
      await fetchStandings(selectedSeason || null);
    } catch (err: any) {
      setError(err.message || "An error occurred while deleting the game");
      console.error("Error deleting game:", err);
      throw err; // Propagate error to component for handling
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    games,
    standings,
    clubs,
    isLoading,
    error,
    fetchGames,
    fetchStandings,
    fetchClubs,
    updateGame,
    createGame,
    deleteGame,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
}

export function useSchedule() {
  const context = useContext(ScheduleContext);
  if (context === undefined) {
    throw new Error("useSchedule must be used within a ScheduleProvider");
  }
  return context;
}
